import React, { useState, useEffect } from "react";
import { useRequestUrl } from "../context/contextHooks";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery'
import API from '../http/API';
import {useStyles} from "../styles/styles";
import { getReceiptRequest } from "./paymentFunctions";
import { getCurrency } from "../global/utils";
import Dialog from '@mui/material/Dialog';
import DialogActions from "@mui/material/DialogActions";
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Replay from "@mui/icons-material/Replay";
import Typography from '@mui/material/Typography';
import LineItems from "./LineItems";
import UnpaidBalances from "./UnpaidBalances";
import {Download} from "@mui/icons-material";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

const PaymentDetails = (props) => {
    const paymentRequest = props.paymentRequest;
    const paymentResult = props.paymentResult;
    const openReceipt = props.openReceipt;
    const setOpenReceipt = props.setOpenReceipt;
    const court = props.court;
    const classes = useStyles;
    const theme = useTheme();
    const tiny = useMediaQuery(theme.breakpoints.down('sm'));
    const [isDownloading, setIsDownloading] = useState(false);
    const [fileResponse, setFileResponse] = useState(null);
    const [requestUrl,] = useRequestUrl();
    const [error, setError] = useState(null);

    const handleClose = (event, reason) => {
        if(reason === null || (reason !== 'backdropClick' && reason !== 'escapeKeyDown')) {
            setOpenReceipt(false);
            setIsDownloading(false);
            setError(null);
        }
    }

    const handleDownload = () => {
        setError(null);
        if (!isDownloading) {
            setIsDownloading(true);
            const request = getReceiptRequest(paymentRequest, paymentResult);
            API.postForFile(`${requestUrl}/getReceiptPdf`, request, 'getReceipt', null, setFileResponse);
        }
    };

    const scrollToAlert = () => {
        window.scrollTo(0, 40);
    };

    useEffect(() => {
        if (isDownloading && fileResponse) {
            if (fileResponse.type === 'application/json') {
                const blb = new Blob([fileResponse], {type: "application/json"});
                const reader = new FileReader();
                reader.onload = function () {
                    const text = reader.result;
                    const actionResponse = JSON.parse(text);
                    setError(actionResponse.errorMessage);
                    scrollToAlert();
                }
                reader.readAsText(blb);
            } else {
                const file = new Blob([fileResponse], {type: "application/pdf"});
                file.name = 'paymentsReport.pdf'
                const fileUrl = window.URL.createObjectURL(file);
                let link = document.createElement('a');
                link.href = fileUrl;
                link.download = 'CitePayReceipt.pdf';
                link.click();
                setTimeout(() => {
                    window.URL.revokeObjectURL(fileUrl);
                }, 100);
            }
            setIsDownloading(false);
        }
    }, [fileResponse])

    // MUI v9 removed disableEscapeKeyDown. No behavior change here: handleClose
    // already ignores the 'escapeKeyDown' and 'backdropClick' reasons.
    return (
        <Dialog
            fullWidth
            maxWidth='sm'
            open={openReceipt}
            onClose={handleClose}
            sx={classes.dialog}
        >
            <DialogTitle style={{paddingBottom: '0'}}>
                {/* DialogTitle already renders an h2, so a heading here nested one
                    inside the other. A block span keeps the appearance identical
                    and leaves the DialogTitle as the single heading. */}
                <Typography variant="h6" component='span' sx={{...classes.title, display: 'block'}}>Payment Details</Typography>
            </DialogTitle>
            { error &&
                <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                    <AlertTitle>Error!</AlertTitle>
                    {error}
                </Alert>
            }
            <DialogContent dividers sx={{minHeight: '50vh'}}>
                <Grid container spacing={2} sx={{
                    alignItems: 'top'
                }} >
                    <Grid size={5}>
                        <Typography component='span' sx={classes.displayLabel}>Time: </Typography>
                    </Grid>
                    <Grid size={7}>
                        <Typography component='span' color='primary' sx={classes.displayValue}>{paymentResult.zonedPaymentTime}</Typography>
                    </Grid>
                    <Grid size={5}>
                        <Typography component='span' sx={classes.displayLabel}>{tiny? 'Trans. ID: ' : 'Transaction ID: '}</Typography>
                    </Grid>
                    <Grid size={7}>
                        <Typography component='span' color='primary' sx={classes.displayValue}>{paymentResult.transactionId}</Typography>
                    </Grid>
                    <Grid size={5}>
                        <Typography component='span' sx={classes.displayLabel}>Payment Amount: </Typography>
                    </Grid>
                    <Grid size={7}>
                        <Typography component='span' color='primary' sx={classes.displayValue}>{getCurrency(paymentRequest.totalAmount)}</Typography>
                    </Grid>
                    { paymentResult.authCode &&
                        <>
                            <Grid size={5}>
                                <Typography component='span' sx={classes.displayLabel}>Approval Code: </Typography>
                            </Grid>
                            <Grid size={7}>
                                <Typography component='span' color='primary' sx={classes.displayValue}>{paymentResult.authCode}</Typography>
                            </Grid>
                        </>
                    }
                    { paymentResult.cardType &&
                        <>
                            <Grid size={5}>
                                <Typography component='span' sx={classes.displayLabel}>Card Type: </Typography>
                            </Grid>
                            <Grid size={7}>
                                <Typography component='span' color='primary' sx={classes.displayValue}>{paymentResult.cardType}</Typography>
                            </Grid>
                        </>
                    }
                    { paymentResult.cardNumberMasked &&
                        <>
                            <Grid size={5}>
                                <Typography component='span' sx={classes.displayLabel}>Account Number: </Typography>
                            </Grid>
                            <Grid size={7}>
                                <Typography component='span' color='primary' sx={classes.displayValue}>{paymentResult.cardNumberMasked}</Typography>
                            </Grid>
                        </>
                    }
                    <Grid size={5}>
                        <Typography component='span' sx={classes.displayLabel}>{tiny ? 'Desc.: ' : 'Description: '}</Typography>
                    </Grid>
                    <Grid size={7}>
                        <Typography component='span' sx={classes.displayValue}>{paymentRequest.paymentDescription}</Typography>
                    </Grid>
                    { paymentRequest.serviceFee && paymentRequest.serviceFee > 0 &&
                        <Grid size={12}>
                            <Typography variant='body1' component='div' color='secondary'>
                                {`A payment plan service fee of $${paymentRequest.serviceFee} has been added.`}
                            </Typography>
                        </Grid>
                    }
                    { ((paymentRequest.balances && paymentRequest.balances.length > 0) || (paymentRequest.plans && paymentRequest.plans.length > 0)) ?
                        <Grid sx={{paddingLeft: '2rem'}} size={12}>
                            <LineItems balances={paymentRequest.balances} plans={paymentRequest.plans} feeAmount={paymentRequest.feeAmount} />
                        </Grid> :
                        <>
                            <Grid size={5}>
                                <Typography component='span' sx={classes.displayLabel}>CitePay Fee: </Typography>
                            </Grid>
                            <Grid size={7}>
                                <Typography component='span' sx={classes.displayValue}>{getCurrency(paymentRequest.feeAmount)}</Typography>
                            </Grid>
                        </>
                    }
                    { paymentRequest.unpaidBalances &&
                        paymentRequest.unpaidBalances.length > 0 &&
                        !court.receiptNoUnpaid &&
                        (!paymentRequest.paymentPlans || paymentRequest.paymentPlans.length === 0) &&
                        <UnpaidBalances unpaidBalances={paymentRequest.unpaidBalances} />
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined"
                        color="primary"
                        startIcon={<Replay/>}
                        sx={classes.button}
                        onClick={handleClose}
                >
                    Close
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Download/>}
                    sx={classes.button}
                    onClick={handleDownload}
                    disabled={isDownloading}
                >
                    {isDownloading ? "Downloading..." : "Download"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default PaymentDetails;
